import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Lock, DollarSign, Eye, EyeOff, LogOut, FolderKanban, 
  Plus, Trash2, Edit2, MapPin, Calendar, Image, Save, X, Search, Users, Phone, Mail, Building
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_PASSWORD = 'iqes2026admin';

// Sample project data (will be replaced with API data)
const sampleProjects = [
  { id: '1', title: 'Hospital Access Control System', titleEs: 'Sistema de Control de Acceso Hospital', location: 'Tampa', date: '01/15/2025', description: 'Complete access control system for a 200-bed hospital. Card readers for restricted areas and emergency override capabilities.', descriptionEs: 'Sistema completo de control de acceso para un hospital de 200 camas.', category: 'Access Control', image: '/projects/project1.jpeg' },
  { id: '2', title: 'Retail CCTV Security System', titleEs: 'Sistema de Seguridad CCTV Retail', location: 'Orlando', date: '01/20/2025', description: 'State-of-the-art surveillance system with remote monitoring for a large shopping center.', descriptionEs: 'Sistema de vigilancia de última generación.', category: 'CCTV', image: '/projects/project2.jpeg' },
  { id: '3', title: 'Corporate Office Fiber Network', titleEs: 'Red de Fibra Oficina Corporativa', location: 'Jacksonville', date: '01/19/2025', description: 'Design & design network installation for a large corporate HQ.', descriptionEs: 'Instalación de red de fibra para oficina corporativa.', category: 'Fiber Optic', image: '/projects/project3.jpeg' },
  { id: '4', title: 'Warehouse Infrastructure Installation', titleEs: 'Instalación Infraestructura Almacén', location: 'Miami', date: '01/10/2025', description: 'Complete infrastructure for warehouse facility.', descriptionEs: 'Infraestructura completa para almacén.', category: 'Structured Cabling', image: '/projects/project4.jpeg' },
  { id: '5', title: 'Sports Facility PTZ Camera System', titleEs: 'Sistema Cámaras PTZ Instalación Deportiva', location: 'Tampa', date: '01/08/2025', description: 'Advanced PTZ camera system for sports facility.', descriptionEs: 'Sistema de cámaras PTZ para instalación deportiva.', category: 'CCTV', image: '/projects/project5.jpeg' },
  { id: '6', title: 'Enterprise Data Center Network', titleEs: 'Red Centro de Datos Empresarial', location: 'Orlando', date: '01/05/2025', description: 'High-speed network infrastructure for enterprise data center.', descriptionEs: 'Infraestructura de red de alta velocidad.', category: 'Networking', image: '/projects/project6.jpeg' },
];

// Price categories
const priceCategories = [
  { id: 'all', label: 'All Categories', labelEs: 'Todas' },
  { id: 'fiber', label: 'Fiber Optic', labelEs: 'Fibra Óptica' },
  { id: 'cctv', label: 'CCTV/Security', labelEs: 'CCTV/Seguridad' },
  { id: 'access', label: 'Access Control', labelEs: 'Control de Acceso' },
  { id: 'cabling', label: 'Structured Cabling', labelEs: 'Cableado' },
  { id: 'networking', label: 'Networking & WiFi', labelEs: 'Redes y WiFi' },
];

// Sample price items
const samplePriceItems = [
  { id: '1', category: 'fiber', name: 'Install Trunk cable per corridor', nameEs: 'Instalar cable troncal por corredor', unit: 'per unit', min: 595, mix: 855, avg: 650, base: 535, active: true },
  { id: '2', category: 'fiber', name: 'Install Compute cable in cable conduit-1/2 inch', nameEs: 'Instalar cable en conducto', unit: 'per foot', min: 105, mix: 155, avg: 120, base: 95, active: true },
  { id: '3', category: 'cctv', name: 'Install 4K Ultra HD PTZ Camera', nameEs: 'Instalar cámara PTZ 4K', unit: 'each', min: 850, mix: 1200, avg: 950, base: 750, active: true },
  { id: '4', category: 'access', name: 'Install Basic Electronic Lock', nameEs: 'Instalar cerradura electrónica', unit: 'per door', min: 395, mix: 550, avg: 450, base: 350, active: true },
  { id: '5', category: 'access', name: 'Install Biometric System', nameEs: 'Instalar sistema biométrico', unit: 'each', min: 695, mix: 950, avg: 800, base: 600, active: true },
  { id: '6', category: 'cabling', name: 'Install Simplex Access Control', nameEs: 'Instalar control de acceso simplex', unit: 'per point', min: 125, mix: 185, avg: 150, base: 100, active: true },
  { id: '7', category: 'networking', name: 'Install Gateway Access Control', nameEs: 'Instalar gateway de control', unit: 'each', min: 495, mix: 695, avg: 575, base: 425, active: true },
];

export default function Admin() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [activeTab, setActiveTab] = useState('projects');
  const [projects, setProjects] = useState(sampleProjects);
  const [priceItems, setPriceItems] = useState(samplePriceItems);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [editingPrice, setEditingPrice] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);

  useEffect(() => {
    const auth = sessionStorage.getItem('iqes_admin_auth');
    if (auth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  // Fetch leads when authenticated and leads tab is active
  useEffect(() => {
    if (isAuthenticated && activeTab === 'leads') {
      fetchLeads();
    }
  }, [isAuthenticated, activeTab]);

  const fetchLeads = async () => {
    setLoadingLeads(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/leads`);
      if (response.ok) {
        const data = await response.json();
        setLeads(data.leads || data || []);
      } else {
        toast.error('Error al cargar leads');
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Error de conexión');
    } finally {
      setLoadingLeads(false);
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem('iqes_admin_auth', 'true');
      toast.success('Bienvenido al panel de administración');
    } else {
      toast.error('Contraseña incorrecta');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('iqes_admin_auth');
    navigate('/');
  };

  // Project handlers
  const handleSaveProject = (projectData) => {
    if (editingProject) {
      setProjects(projects.map(p => p.id === editingProject.id ? { ...projectData, id: editingProject.id } : p));
      toast.success('Proyecto actualizado');
    } else {
      setProjects([...projects, { ...projectData, id: Date.now().toString() }]);
      toast.success('Proyecto agregado');
    }
    setShowProjectModal(false);
    setEditingProject(null);
  };

  const handleDeleteProject = (id) => {
    if (window.confirm('¿Está seguro de eliminar este proyecto?')) {
      setProjects(projects.filter(p => p.id !== id));
      toast.success('Proyecto eliminado');
    }
  };

  // Price handlers
  const handleSavePrice = (priceData) => {
    if (editingPrice) {
      setPriceItems(priceItems.map(p => p.id === editingPrice.id ? { ...priceData, id: editingPrice.id } : p));
      toast.success('Precio actualizado');
    } else {
      setPriceItems([...priceItems, { ...priceData, id: Date.now().toString() }]);
      toast.success('Precio agregado');
    }
    setShowPriceModal(false);
    setEditingPrice(null);
  };

  const handleDeletePrice = (id) => {
    if (window.confirm('¿Está seguro de eliminar este precio?')) {
      setPriceItems(priceItems.filter(p => p.id !== id));
      toast.success('Precio eliminado');
    }
  };

  // Filter prices
  const filteredPrices = priceItems.filter(item => {
    const matchCategory = selectedCategory === 'all' || item.category === selectedCategory;
    const matchSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                       item.nameEs.toLowerCase().includes(searchTerm.toLowerCase());
    return matchCategory && matchSearch;
  });

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white shadow-lg">
          <CardHeader className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#003366] flex items-center justify-center">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl text-gray-800">Panel de Administración</CardTitle>
            <p className="text-gray-500 mt-2">Ingrese su contraseña para continuar</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Contraseña"
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#003366]"
                  data-testid="admin-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <Button 
                type="submit" 
                className="w-full bg-[#003366] hover:bg-[#002244] text-white"
                data-testid="admin-login-btn"
              >
                Ingresar
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="flex justify-between items-center px-6 py-3">
          <div className="flex items-center gap-3">
            <img src="/logo-iqes-new.png" alt="IQES" className="h-10" />
            <span className="text-gray-400">|</span>
            <span className="text-gray-600 font-medium">Panel de Administración</span>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleLogout}
            className="border-gray-300 text-gray-600 hover:bg-gray-100"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Salir
          </Button>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-white border-r border-gray-200 min-h-[calc(100vh-64px)]">
          <nav className="p-4 space-y-2">
            <button
              onClick={() => setActiveTab('projects')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all ${
                activeTab === 'projects'
                  ? 'bg-orange-50 text-orange-600 border-l-4 border-orange-500'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              data-testid="admin-tab-projects"
            >
              <FolderKanban className="w-5 h-5" />
              <span className="font-medium">Gestión de Proyectos</span>
            </button>
            <button
              onClick={() => setActiveTab('prices')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all ${
                activeTab === 'prices'
                  ? 'bg-orange-50 text-orange-600 border-l-4 border-orange-500'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              data-testid="admin-tab-prices"
            >
              <DollarSign className="w-5 h-5" />
              <span className="font-medium">Gestión de Precios</span>
            </button>
            <button
              onClick={() => setActiveTab('leads')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all ${
                activeTab === 'leads'
                  ? 'bg-orange-50 text-orange-600 border-l-4 border-orange-500'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              data-testid="admin-tab-leads"
            >
              <Users className="w-5 h-5" />
              <span className="font-medium">Leads / Contactos</span>
            </button>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6">
          {/* Projects Tab */}
          {activeTab === 'projects' && (
            <div>
              {/* Header */}
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-xl font-bold text-orange-500 flex items-center gap-2">
                    <FolderKanban className="w-5 h-5" />
                    Gestión de Proyectos
                  </h2>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="border-orange-500 text-orange-500 hover:bg-orange-50"
                  >
                    <Search className="w-4 h-4 mr-2" />
                    Buscar
                  </Button>
                  <Button 
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                    onClick={() => { setEditingProject(null); setShowProjectModal(true); }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Nuevo Proyecto
                  </Button>
                </div>
              </div>

              {/* Projects Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {projects.map((project) => (
                  <Card key={project.id} className="bg-white border border-gray-200 hover:shadow-lg transition-shadow overflow-hidden">
                    {/* Project Image */}
                    <div className="h-40 bg-gray-100 relative">
                      {project.image ? (
                        <img src={project.image} alt={project.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Image className="w-12 h-12 text-gray-300" />
                        </div>
                      )}
                      <div className="absolute top-2 right-2 flex gap-1">
                        <span className="bg-orange-500 text-white text-xs px-2 py-1 rounded">
                          {project.category}
                        </span>
                      </div>
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-bold text-gray-800 mb-2">{project.title}</h3>
                      <div className="flex items-center gap-4 text-sm text-gray-500 mb-2">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {project.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {project.date}
                        </span>
                      </div>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">{project.description}</p>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="flex-1 border-orange-500 text-orange-500 hover:bg-orange-50"
                          onClick={() => { setEditingProject(project); setShowProjectModal(true); }}
                        >
                          <Edit2 className="w-4 h-4 mr-1" />
                          Editar
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="border-red-500 text-red-500 hover:bg-red-50"
                          onClick={() => handleDeleteProject(project.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Prices Tab */}
          {activeTab === 'prices' && (
            <div>
              {/* Header */}
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-xl font-bold text-green-600 flex items-center gap-2">
                    <DollarSign className="w-5 h-5" />
                    Price List Manager
                  </h2>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="border-green-500 text-green-500 hover:bg-green-50"
                  >
                    <Search className="w-4 h-4 mr-2" />
                    Exportar
                  </Button>
                  <Button 
                    className="bg-green-500 hover:bg-green-600 text-white"
                    onClick={() => { setEditingPrice(null); setShowPriceModal(true); }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add New
                  </Button>
                </div>
              </div>

              {/* Search and Filter */}
              <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
                <div className="flex gap-4 items-center mb-4">
                  <div className="flex-1 relative">
                    <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search by name, category, or part..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green-500"
                    />
                  </div>
                  <Button className="bg-green-500 hover:bg-green-600 text-white">
                    All Search
                  </Button>
                </div>
                {/* Category Tabs */}
                <div className="flex gap-2 flex-wrap">
                  {priceCategories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setSelectedCategory(cat.id)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        selectedCategory === cat.id
                          ? 'bg-orange-500 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Price Table */}
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Category</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Items</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Unit</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Min</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Mix</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Avg</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Base</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredPrices.map((item) => {
                      const category = priceCategories.find(c => c.id === item.category);
                      return (
                        <tr key={item.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-orange-100 text-orange-600">
                              {category?.label || item.category}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-medium text-gray-800 text-sm">{item.name}</p>
                              <p className="text-xs text-gray-500">{item.nameEs}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">{item.unit}</td>
                          <td className="px-4 py-3 text-right text-sm font-medium">${item.min}</td>
                          <td className="px-4 py-3 text-right text-sm font-medium">${item.mix}</td>
                          <td className="px-4 py-3 text-right text-sm font-medium">${item.avg}</td>
                          <td className="px-4 py-3 text-right text-sm font-medium">${item.base}</td>
                          <td className="px-4 py-3">
                            <div className="flex justify-center gap-1">
                              <button
                                onClick={() => { setEditingPrice(item); setShowPriceModal(true); }}
                                className="p-1 text-blue-500 hover:bg-blue-50 rounded"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeletePrice(item.id)}
                                className="p-1 text-red-500 hover:bg-red-50 rounded"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Leads Tab */}
          {activeTab === 'leads' && (
            <div>
              {/* Header */}
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-xl font-bold text-orange-500 flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Leads / Contactos
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {leads.length} leads capturados
                  </p>
                </div>
                <Button 
                  onClick={fetchLeads}
                  variant="outline" 
                  className="border-orange-500 text-orange-500 hover:bg-orange-50"
                >
                  Actualizar
                </Button>
              </div>

              {/* Leads Table */}
              {loadingLeads ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                </div>
              ) : leads.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No hay leads capturados aún</p>
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contacto</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Servicio</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fuente</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {leads.map((lead, index) => (
                        <tr key={lead.id || lead.lead_id || index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {lead.created_at ? new Date(lead.created_at).toLocaleDateString('es-ES', {
                              day: '2-digit',
                              month: '2-digit',
                              year: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) : 'N/A'}
                          </td>
                          <td className="px-4 py-3">
                            <p className="font-medium text-gray-800">{lead.name || 'Sin nombre'}</p>
                            {lead.company && <p className="text-xs text-gray-500">{lead.company}</p>}
                          </td>
                          <td className="px-4 py-3">
                            <div className="space-y-1">
                              {lead.email && (
                                <a href={`mailto:${lead.email}`} className="flex items-center gap-1 text-sm text-blue-600 hover:underline">
                                  <Mail className="w-3 h-3" />
                                  {lead.email}
                                </a>
                              )}
                              {lead.phone && (
                                <a href={`tel:${lead.phone}`} className="flex items-center gap-1 text-sm text-green-600 hover:underline">
                                  <Phone className="w-3 h-3" />
                                  {lead.phone}
                                </a>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-orange-100 text-orange-600">
                              {lead.service_interest || lead.service || 'General'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded ${
                              lead.source === 'retell_voice' ? 'bg-purple-100 text-purple-600' :
                              lead.source === 'chat' ? 'bg-blue-100 text-blue-600' :
                              lead.source === 'quote_form' ? 'bg-green-100 text-green-600' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {lead.source === 'retell_voice' ? 'Llamada' :
                               lead.source === 'chat' ? 'Chat' :
                               lead.source === 'quote_form' ? 'Formulario' :
                               lead.source || 'Web'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded ${
                              lead.status === 'new' ? 'bg-yellow-100 text-yellow-600' :
                              lead.status === 'contacted' ? 'bg-blue-100 text-blue-600' :
                              lead.status === 'converted' ? 'bg-green-100 text-green-600' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {lead.status === 'new' ? 'Nuevo' :
                               lead.status === 'contacted' ? 'Contactado' :
                               lead.status === 'converted' ? 'Convertido' :
                               lead.status || 'Nuevo'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </main>
      </div>

      {/* Project Modal */}
      {showProjectModal && (
        <ProjectModal
          project={editingProject}
          onSave={handleSaveProject}
          onClose={() => { setShowProjectModal(false); setEditingProject(null); }}
        />
      )}

      {/* Price Modal */}
      {showPriceModal && (
        <PriceModal
          price={editingPrice}
          categories={priceCategories.filter(c => c.id !== 'all')}
          onSave={handleSavePrice}
          onClose={() => { setShowPriceModal(false); setEditingPrice(null); }}
        />
      )}
    </div>
  );
}

// Project Modal Component
function ProjectModal({ project, onSave, onClose }) {
  const [formData, setFormData] = useState({
    title: project?.title || '',
    titleEs: project?.titleEs || '',
    location: project?.location || '',
    date: project?.date || '',
    description: project?.description || '',
    descriptionEs: project?.descriptionEs || '',
    category: project?.category || 'Fiber Optic',
    image: project?.image || '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-bold text-gray-800">
            {project ? 'Editar Proyecto' : 'Nuevo Proyecto'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Título (English)*</label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-orange-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Título (Spanish)</label>
              <input
                type="text"
                value={formData.titleEs}
                onChange={(e) => setFormData({ ...formData, titleEs: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-orange-500"
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Servicio*</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-orange-500"
              >
                <option value="Fiber Optic">Fiber Optic / Fibra Óptica</option>
                <option value="CCTV">CCTV / Videovigilancia</option>
                <option value="Structured Cabling">Structured Cabling / Cableado</option>
                <option value="Access Control">Access Control / Control de Acceso</option>
                <option value="Networking">Networking / Redes</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ubicación*</label>
              <input
                type="text"
                required
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-orange-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de Realización</label>
              <input
                type="text"
                placeholder="01/15/2025"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-orange-500"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descripción (Inglés)*</label>
              <textarea
                required
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-orange-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descripción (Español)</label>
              <textarea
                rows={3}
                value={formData.descriptionEs}
                onChange={(e) => setFormData({ ...formData, descriptionEs: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-orange-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Galería de Fotos</label>
            <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center">
              <Image className="w-10 h-10 mx-auto text-gray-300 mb-2" />
              <p className="text-gray-500 text-sm">Arrastra tus imágenes aquí</p>
              <p className="text-gray-400 text-xs mt-1">o haz clic para seleccionar</p>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" className="bg-orange-500 hover:bg-orange-600 text-white">
              <Save className="w-4 h-4 mr-2" />
              Guardar Cambios
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Price Modal Component
function PriceModal({ price, categories, onSave, onClose }) {
  const [formData, setFormData] = useState({
    category: price?.category || 'fiber',
    name: price?.name || '',
    nameEs: price?.nameEs || '',
    unit: price?.unit || 'each',
    min: price?.min || 0,
    mix: price?.mix || 0,
    avg: price?.avg || 0,
    base: price?.base || 0,
    active: price?.active ?? true,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-lg">
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-bold text-gray-800">
            {price ? 'Edit Price Item' : 'Add New Price Item'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category*</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green-500"
              >
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Unit Type</label>
              <select
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green-500"
              >
                <option value="each">Per Unit</option>
                <option value="per foot">Per Foot</option>
                <option value="per door">Per Door</option>
                <option value="per point">Per Point</option>
                <option value="per hour">Per Hour</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name (English)*</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name (Spanish)</label>
              <input
                type="text"
                value={formData.nameEs}
                onChange={(e) => setFormData({ ...formData, nameEs: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green-500"
              />
            </div>
          </div>
          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Min Price ($)</label>
              <input
                type="number"
                value={formData.min}
                onChange={(e) => setFormData({ ...formData, min: Number(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Price ($)</label>
              <input
                type="number"
                value={formData.mix}
                onChange={(e) => setFormData({ ...formData, mix: Number(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Setup Fee ($)</label>
              <input
                type="number"
                value={formData.avg}
                onChange={(e) => setFormData({ ...formData, avg: Number(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Unit Cost ($)</label>
              <input
                type="number"
                value={formData.base}
                onChange={(e) => setFormData({ ...formData, base: Number(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green-500"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="active"
              checked={formData.active}
              onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
              className="rounded border-gray-300"
            />
            <label htmlFor="active" className="text-sm text-gray-600">Active</label>
            <span className="text-xs text-gray-400 ml-2">• Show in Quote • Show in Dynamic</span>
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" className="bg-green-500 hover:bg-green-600 text-white">
              Add Price
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
